
import React, { useEffect, useRef } from 'react';
import { Award, BadgeDollarSign, Clock, Monitor, Users, CirclePlay } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { Link } from 'react-router-dom';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';

const PricingContent = () => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const sectionRef = useRef<HTMLElement>(null);
  const premiumSectionRef = useRef<HTMLDivElement>(null);
  const { getWhyFutureLevelPath, getPricingPath, getContactPath } = useNavigationUtils();
  
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const cardElements = entry.target.querySelectorAll('.animate-on-scroll');
          cardElements.forEach((el, index) => {
            setTimeout(() => {
              el.classList.add('animate-fade-in');
            }, index * 150);
          });
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    if (premiumSectionRef.current) {
      observer.observe(premiumSectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      if (premiumSectionRef.current) {
        observer.unobserve(premiumSectionRef.current);
      }
    };
  }, []);

  // Premium package data
  const premiumPackages = [
    {
      title: 'Basis-Paket',
      price: 'Ab 350 CHF',
      features: [
        '1 KI-generiertes Video (30-60 Sek.)',
        '1 Revisionsrunde inklusive (jede weitere: 80 CHF)',
        'Grundlegende Anpassungen'
      ]
    },
    {
      title: 'Business-Paket',
      price: 'Ab 850 CHF',
      features: [
        '3 KI-generierte Videos',
        '2 Revisionsrunden pro Video inklusive',
        'Individuelle Anpassungen mit Premium-KI-Tools'
      ],
      highlight: true
    },
    {
      title: 'Premium-Paket',
      price: 'Ab 1.600 CHF',
      features: [
        '5 KI-generierte Premium-Videos',
        '3 Revisionsrunden pro Video inklusive',
        'Vollständige Anpassung mit neuester KI-Technologie',
        'Strategische Beratung inklusive'
      ]
    }
  ];

  // Social Media packages
  const socialMediaPackages = [
    {
      title: 'Starter-Paket',
      price: 'Ab 450 CHF',
      features: [
        '2 Social Media Ad-Videos (15-30 Sek.)',
        '1 Revisionsrunde inklusive',
        'Optimiert für 1 Social Media Plattform'
      ]
    },
    {
      title: 'Growth-Paket',
      price: 'Ab 950 CHF',
      features: [
        '4 Social Media Ad-Videos',
        '2 Revisionsrunden pro Video inklusive',
        'Optimiert für 2 Social Media Plattformen',
        'A/B Testing für 2 Varianten'
      ],
      highlight: true
    },
    {
      title: 'Enterprise-Paket',
      price: 'Ab 1.800 CHF',
      features: [
        '6 Social Media Ad-Videos',
        '3 Revisionsrunden pro Video inklusive',
        'Optimiert für alle wichtigen Plattformen',
        'A/B Testing für 3 Varianten',
        'Social Media Strategie-Beratung inklusive'
      ]
    }
  ];

  // Premium benefits
  const premiumBenefits = [
    {
      icon: Award,
      title: 'Internationale KI-Expertise',
      description: 'Globales Know-how in fortschrittlicher KI-Videoproduktion direkt zu Schweizer Unternehmen'
    },
    {
      icon: Monitor,
      title: 'Fortschrittliche KI-Tools',
      description: 'Enterprise-Level KI-Videogeneratoren und Premium-Softwarelizenzen für beste Ergebnisse'
    },
    {
      icon: BadgeDollarSign,
      title: 'Erhebliche Kosteneinsparung',
      description: 'Vergleichbare Qualität zu einem Bruchteil der Kosten traditioneller Videoproduktionen'
    },
    {
      icon: Clock,
      title: 'Schnellere Markteinführung',
      description: 'Lieferung in Tagen statt Wochen – ein entscheidender Wettbewerbsvorteil'
    },
    {
      icon: CirclePlay,
      title: 'Innovation trifft Schweizer Standards',
      description: 'KI-Pionierarbeit verbunden mit Schweizer Zuverlässigkeit und Präzision'
    },
    {
      icon: Users,
      title: 'Mehrsprachige Expertise',
      description: 'Mehrsprachige Inhalte für die multikulturelle Schweiz und internationale Märkte'
    }
  ];

  return (
    <section 
      ref={sectionRef} 
      className="py-16 md:py-24 bg-gradient-to-b from-[#0B0B0B] to-[#101010] relative overflow-hidden" 
      id="pricing"
    >
      {/* Dynamic animated particles background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Flowing data particles */}
        <div className="absolute inset-0 opacity-10">
          {Array(15).fill(0).map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full bg-gradient-to-r from-purple-500/40 to-cyan-400/40 blur-xl"
              style={{
                width: Math.random() * 200 + 50 + 'px',
                height: Math.random() * 200 + 50 + 'px',
                left: Math.random() * 100 + '%',
                top: Math.random() * 100 + '%',
                opacity: Math.random() * 0.3 + 0.2,
                animation: `parallax-shift ${Math.random() * 20 + 10}s ease-in-out infinite ${Math.random() * 5}s`,
                transform: `scale(${Math.random() * 0.5 + 0.5})`,
              }}
            />
          ))}
        </div>
        
        {/* Neural network lines/Digital waves */}
        <div className="absolute inset-0 opacity-15">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="networkGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#9333EA" stopOpacity="0.6"/>
                <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.4"/>
              </linearGradient>
            </defs>
            {Array(10).fill(0).map((_, i) => (
              <path 
                key={i} 
                d={`M${Math.random() * 100},${Math.random() * 100} Q${Math.random() * 100},${Math.random() * 100} ${Math.random() * 100},${Math.random() * 100}`}
                stroke="url(#networkGradient)"
                strokeWidth="0.5"
                fill="none"
                className="svg-drawing-animation"
              />
            ))}
          </svg>
        </div>
        
        {/* Center vertical glow */}
        <div className="absolute left-1/2 top-0 bottom-0 w-[60%] -translate-x-1/2 bg-gradient-to-t from-purple-900/10 via-purple-500/15 to-purple-900/10 blur-3xl pointer-events-none"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        {/* KI-Produktvideos Pricing Section */}
        <div 
          ref={premiumSectionRef}
          className="mb-24"
        >
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-future tracking-wider text-white micro-glitch">
              Premium KI-Video-Produktion
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Unsere Premium KI-Video-Pakete
            </p>
            <div className="w-3 h-3 rounded-full bg-purple-500 mx-auto mt-6 animate-pulse"></div>
          </div>
          
          {/* Premium Packages Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            {premiumPackages.map((pkg, index) => (
              <div 
                key={index}
                className={`animate-on-scroll backdrop-blur-lg ${pkg.highlight ? 'bg-gradient-to-b from-purple-900/40 to-purple-800/20 border-purple-500/50' : 'bg-black/70 border-purple-500/30'} border rounded-xl p-6 transition-all duration-500 hover:transform hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(139,92,246,0.4)]`}
                style={{ 
                  animationDelay: `${index * 0.15}s`,
                  opacity: 1,
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                }}
              >
                <h3 className="text-xl font-semibold mb-3 font-future tracking-wide text-white">{pkg.title}</h3>
                <div className="text-2xl font-bold text-purple-300 mb-5">{pkg.price}</div>
                <ul className="space-y-3 mb-6 text-gray-300 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-purple-400 mr-2">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 mt-auto">
                  <Link to={getWhyFutureLevelPath()} className="block w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors duration-300 text-center">
                    {t.learnMore}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Social Media Ads Pricing Section */}
        <div className="mb-24">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 font-future tracking-wider text-white micro-glitch">
              Social Media Ad-Videos
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Hochperformante Video-Anzeigen für Ihre Social Media Kanäle
            </p>
            <div className="w-3 h-3 rounded-full bg-purple-500 mx-auto mt-6 animate-pulse"></div>
          </div>
          
          {/* Social Media Packages Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            {socialMediaPackages.map((pkg, index) => (
              <div 
                key={index}
                className={`animate-on-scroll backdrop-blur-lg ${pkg.highlight ? 'bg-gradient-to-b from-indigo-900/40 to-indigo-800/20 border-indigo-500/50' : 'bg-black/70 border-indigo-500/30'} border rounded-xl p-6 transition-all duration-500 hover:transform hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(99,102,241,0.4)]`}
                style={{ 
                  animationDelay: `${index * 0.15}s`,
                  opacity: 1,
                  transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
                }}
              >
                <h3 className="text-xl font-semibold mb-3 font-future tracking-wide text-white">{pkg.title}</h3>
                <div className="text-2xl font-bold text-indigo-300 mb-5">{pkg.price}</div>
                <ul className="space-y-3 mb-6 text-gray-300 flex-grow">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-indigo-400 mr-2">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="pt-4 mt-auto">
                  <Link to={getWhyFutureLevelPath()} className="block w-full py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors duration-300 text-center">
                    {t.learnMore}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly Subscription */}
        <div className="max-w-3xl mx-auto mb-20 animate-on-scroll">
          <div className="bg-gradient-to-r from-purple-900/30 to-indigo-900/30 backdrop-blur-md border border-purple-500/30 rounded-xl p-6 text-center">
            <h3 className="text-xl font-semibold mb-3 font-future tracking-wide text-white">Monatliche Abonnements</h3>
            <p className="text-gray-300">
              Für Unternehmen, die regelmäßig hochwertige KI-Videoinhalte benötigen, bieten wir exklusive monatliche Abonnements ab 2.000 CHF an.
            </p>
            <Link to={getContactPath()} className="inline-block mt-4 py-2 px-6 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors duration-300">
              {t.contactUs}
            </Link>
          </div>
        </div>

        {/* Why Premium Section */}
        <div className="mb-16 animate-on-scroll">
          <h3 className="text-2xl font-semibold mb-10 font-future tracking-wide text-white text-center">
            Warum unsere Premium-Preisgestaltung?
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {premiumBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="flex flex-col items-center text-center p-6 backdrop-blur-sm bg-black/40 border border-purple-500/20 rounded-lg transition-all duration-300 hover:border-purple-500/40"
                style={{ 
                  animationDelay: `${index * 0.1}s`,
                  opacity: 1,
                  transition: 'all 0.4s ease-out',
                }}
              >
                <div className="p-3 mb-4 bg-purple-900/30 rounded-full">
                  <benefit.icon size={24} className="text-purple-300" />
                </div>
                <h4 className="text-lg font-semibold mb-2 text-white">{benefit.title}</h4>
                <p className="text-gray-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center max-w-3xl mx-auto text-gray-300 animate-on-scroll">
          <p className="mb-6">
            Mit Future Level erhalten Sie internationales KI-Fachwissen kombiniert mit Schweizer Qualitätsstandards – zu einem Preis, der traditionelle Videoproduktionen deutlich unterbietet, während wir gleichzeitig Premium-Qualität und Innovation garantieren.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to={getWhyFutureLevelPath()} className="px-8 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md hover:from-purple-700 hover:to-indigo-700 transition-all duration-300 font-medium">
              {t.learnMore}
            </Link>
            <Link to={getContactPath()} className="px-8 py-3 border border-purple-600 text-white rounded-md hover:bg-purple-600/20 transition-all duration-300 font-medium">
              {t.contactUs}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingContent;
